import { useRef, useEffect, useCallback } from 'react';

// Global tooltip system that follows cursor
let globalTooltipRef: HTMLDivElement | null = null;
let currentMousePosition = { x: 0, y: 0 };
let isGlobalListenerActive = false;

const updateGlobalMousePosition = (event: MouseEvent) => {
  currentMousePosition = { x: event.clientX, y: event.clientY };
  if (globalTooltipRef && globalTooltipRef.style.display !== 'none') {
    globalTooltipRef.style.left = `${currentMousePosition.x + 12}px`;
    globalTooltipRef.style.top = `${currentMousePosition.y - 12}px`;
    globalTooltipRef.style.transform = 'translate(0, -100%)';
  }
};

const ensureGlobalListener = () => {
  if (!isGlobalListenerActive) {
    document.addEventListener('mousemove', updateGlobalMousePosition, {
      passive: true,
    });
    isGlobalListenerActive = true;
  }
};

export interface TooltipInfo {
  show: boolean;
  message: string;
}

export interface UseTooltipFollowCursorOptions {
  delay?: number;
}


export const useTooltipFollowCursor = (
  tooltipInfo?: TooltipInfo,
  options: UseTooltipFollowCursorOptions = {}
) => {
  const { delay = 0 } = options;
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    ensureGlobalListener();
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const showTooltip = useCallback((message: string) => {
    if (tooltipRef.current) {
      globalTooltipRef = tooltipRef.current;
      globalTooltipRef.textContent = message;
      globalTooltipRef.style.display = 'block';
      globalTooltipRef.style.left = `${currentMousePosition.x + 12}px`;
      globalTooltipRef.style.top = `${currentMousePosition.y - 12}px`;
      globalTooltipRef.style.transform = 'translate(0, -100%)';
    }
  }, []);

  const hideTooltip = useCallback(() => {
    if (tooltipRef.current) {
      tooltipRef.current.style.display = 'none';
      if (globalTooltipRef === tooltipRef.current) {
        globalTooltipRef = null;
      }
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    // Hide any previously active tooltip
    if (globalTooltipRef) {
      globalTooltipRef.style.display = 'none';
    }

    if (tooltipInfo?.show && tooltipInfo.message) {
      timeoutRef.current = setTimeout(() => {
        showTooltip(tooltipInfo.message);
      }, delay);
    }
  }, [tooltipInfo, showTooltip, delay]);

  const handleMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    hideTooltip();
  }, [hideTooltip]);

  return {
    tooltipRef,
    currentMousePosition,
    handlers: {
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
    },
    tooltipInfo,
  };
};